import type { Json } from '$/database.types';
import Routes from '@/router/Routes.ts';

export type SessionDisplayStatus = 'passed' | 'caution' | 'in_progress' | 'escalated' | 'complete';

export type SessionDisplayStatusStyle = {
  labelKey: `homePage.displayStatus.${SessionDisplayStatus}`;
  className: string;
};

const DISPLAY_STATUS_STYLES: Record<SessionDisplayStatus, SessionDisplayStatusStyle['className']> = {
  passed: 'font-dm text-sm font-semibold text-emerald-600',
  caution: 'font-dm text-sm font-semibold text-amber-600',
  in_progress: 'font-dm text-sm font-semibold text-amber-600',
  escalated: 'font-dm text-sm font-semibold text-red-600',
  complete: 'font-dm text-sm font-semibold text-emerald-600',
};

export function displayStatusStyle(status: SessionDisplayStatus): SessionDisplayStatusStyle {
  return {
    labelKey: `homePage.displayStatus.${status}`,
    className: DISPLAY_STATUS_STYLES[status],
  };
}

export function formatGovScore(governanceScore: number | null | undefined): string {
  if (governanceScore == null || Number.isNaN(governanceScore)) return '—';
  return (governanceScore * 10).toFixed(1);
}

export function parseStructuredSummary(aiOutput: Json | null | undefined): string | null {
  if (!aiOutput || typeof aiOutput !== 'object' || Array.isArray(aiOutput)) return null;
  const summary = (aiOutput as { structured_summary?: unknown }).structured_summary;
  return typeof summary === 'string' && summary.trim() ? summary.trim() : null;
}

type ResolveDisplayStatusInput = {
  lifecycleStatus: string;
  governanceScore: number | null;
  pendingReview: boolean;
};

export function resolveDisplayStatus({
  lifecycleStatus,
  governanceScore,
  pendingReview,
}: ResolveDisplayStatusInput): SessionDisplayStatus {
  if (lifecycleStatus === 'escalated' || pendingReview) return 'escalated';
  if (lifecycleStatus === 'execution_asset_produced') return 'complete';

  if (governanceScore != null && !Number.isNaN(governanceScore)) {
    return governanceScore >= 0.75 ? 'passed' : 'caution';
  }

  if (lifecycleStatus === 'governance_evaluated') return 'caution';
  if (lifecycleStatus === 'intent_captured' || lifecycleStatus === 'framework_routed') {
    return 'in_progress';
  }

  return 'in_progress';
}

/**
 * Sessions for Current Orientation banner (Story 3 / Figma).
 * Excludes in_progress — unfinished CLARITY stays in table with Open Session only.
 */
export function isSessionFlagged(row: {
  display_status: SessionDisplayStatus;
  pending_review: boolean;
}): boolean {
  return (
    row.pending_review ||
    row.display_status === 'caution' ||
    row.display_status === 'escalated'
  );
}

export type SessionAction = 'open_session' | 'view_record' | 'complete_session' | 'none';

export function resolveSessionAction(
  lifecycleStatus: string,
  pendingReview: boolean,
): SessionAction {
  if (lifecycleStatus === 'intent_captured' || lifecycleStatus === 'framework_routed') {
    return 'open_session';
  }
  if (lifecycleStatus === 'governance_evaluated' && !pendingReview) {
    return 'complete_session';
  }
  if (
    pendingReview ||
    lifecycleStatus === 'execution_asset_produced' ||
    lifecycleStatus === 'escalated' ||
    lifecycleStatus === 'governance_evaluated'
  ) {
    return 'view_record';
  }
  return 'none';
}

/** Read-only execution record (Story 14) — not an editable workflow resume. */
export function buildSessionDetailNavigation(decisionId: string): {
  pathname: string;
  state: { decisionId: string };
} {
  return {
    pathname: Routes.SESSION_DETAIL.replace(':id', decisionId),
    state: { decisionId },
  };
}

/** Resume path after intake: CLARITY until diagnostic commit, then Recommendation Engine. */
export function resolveSessionOpenRoute(lifecycleStatus: string): string {
  if (lifecycleStatus === 'framework_routed') {
    return Routes.RECOMMENDATION_ENGINE;
  }
  return Routes.CLARITY_DIAGNOSTIC_LAYER;
}

